import partition from 'lodash/partition';
import { useMemo } from 'react';

import {
  TypedUseQueryHookResult,
  skipToken,
} from '@reduxjs/toolkit/dist/query/react';

import { useFetchDiagonalCornersQuery } from './diagonalCornersService';

import { AxiosBaseQueryFunction } from '~/api/axiosBaseQuery';
import { CommonProductQueryParams } from '~/api/easy-estimator-api/commonProductQueryParams';
import { useSelectedStore } from '~/api/easy-estimator-api/stores-api/useSelectedStore';
import { shouldFetchFn } from '~/api/utils';
import { DiagonalCornerOptions } from '~/core/enums';
import { getCornerType } from '~/core/helpers';
import { DiagonalCornerModel } from '~/core/models';
import { useAppSelector } from '~/redux/hooks';
import { selectSelectedColor } from '~/redux/slices/color';
import { selectZipcode } from '~/redux/slices/zipcode';

type useDiagonalCornersReturn = Omit<
  TypedUseQueryHookResult<
    DiagonalCornerModel[],
    CommonProductQueryParams,
    AxiosBaseQueryFunction
  >,
  'data' | 'isFetching'
> & {
  diagonalCorners: DiagonalCornerModel[];
  defaultValue: DiagonalCornerModel | undefined;
  isFetchingDiagonalCorners: boolean;
};

export const useDiagonalCorners = (): useDiagonalCornersReturn => {
  const zipCode = useAppSelector(selectZipcode);
  const selectedStore = useSelectedStore();
  const selectedColor = useAppSelector(selectSelectedColor);

  const shouldFetch = shouldFetchFn({
    args: [zipCode, selectedStore, selectedColor],
    allowPartialForEstimate: true,
  });

  const {
    data: diagonalCorners = [],
    isFetching: isFetchingDiagonalCorners,
    ...rest
  } = useFetchDiagonalCornersQuery(
    shouldFetch
      ? {
          params: {
            zipCode,
            storeId: selectedStore?.companyId,
            prodQteGrpID: selectedColor?.qteGrpID,
          },
        }
      : skipToken
  );

  const defaultValue = useMemo(() => {
    const [[defaultValue]] = partition(
      diagonalCorners,
      (dc) => getCornerType(dc) === DiagonalCornerOptions.REMOVE_DIAGONAL_CORNER
    );

    return defaultValue;
  }, [diagonalCorners]);

  return {
    diagonalCorners,
    defaultValue,
    isFetchingDiagonalCorners,
    ...rest,
  };
};
